import { readonly, ref } from "vue";

// One app-wide source of truth for viewport flags.
//
// Components used to derive these from `window.innerWidth` inside a computed.
// `window.innerWidth` is not reactive, so Vue cached the first value and never
// recomputed it — rotating a phone or dragging a desktop window across a
// breakpoint left the stale layout rendered. Observing the breakpoint with
// matchMedia gives the computed a real dependency, and only fires when the
// boundary is actually crossed instead of on every resize frame.
//
// The queries here deliberately mirror the SCSS the components are styled with,
// so the JS flag and the stylesheet flip at exactly the same width — matchMedia
// gives that identical rounding behaviour for free.

// By far the dominant breakpoint in `src/assets/scss`.
export const MOBILE_MEDIA_QUERY = "(max-width: 768px)";

// One observer per distinct query, shared by every caller — components watching
// the same breakpoint share a single listener for the whole app.
const observed = new Map();

// Guarded so the module stays importable from the plain-Node test scripts.
const supported =
  typeof window !== "undefined" && typeof window.matchMedia === "function";

/**
 * Reactive, readonly flag for a CSS media query.
 *
 * Call it once at module scope and read `.value` from a computed; calling it
 * per component instance is safe too, since the observer is shared per query.
 *
 * @param {string} query a CSS media query, e.g. `"(max-width: 400px)"`
 * @returns {import("vue").Ref<boolean>} readonly ref that tracks `query`
 */
export function useMediaQuery(query) {
  const cached = observed.get(query);
  if (cached) return cached;

  const media = supported ? window.matchMedia(query) : null;
  const matches = ref(media ? media.matches : false);

  if (media) {
    media.addEventListener("change", (event) => {
      matches.value = event.matches;
    });
  }

  const flag = readonly(matches);
  observed.set(query, flag);
  return flag;
}

// Not Vuetify's `useDisplay()`: its `mobile` resolves at `lg` (< 1280) and
// `smAndDown` at < 960, and the only way to move either to 768 is overriding the
// global `display.thresholds`, which would shift every `md`/`sm` prop and
// `d-md-*` utility class in the app.
export const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

// For `<script setup>` / composition-API components. Options-API components
// should use the `@/mixins/isMobile` mixin instead, which exposes `is_mobile`.
export function useIsMobile() {
  return isMobile;
}

// Deliberately *not* MOBILE_MEDIA_QUERY (ADR-0038). `isMobile` decides which
// Intake-sidenavigatie renders -- the docked rail or the teleported drawer with
// its edge indicator and peek-hint (ADR-0018). This one decides only what state
// the sidenav opens in, and it sits at the largest tablet-portrait width: every
// iPad portrait (820/834/1024) lands collapsed, every landscape iPad and every
// laptop width lands open. Two flags, one job each -- do not "unify" them.
export const SIDEBAR_DEFAULT_COLLAPSED_MEDIA_QUERY = "(max-width: 1024px)";

export const sidebarDefaultsCollapsed = useMediaQuery(
  SIDEBAR_DEFAULT_COLLAPSED_MEDIA_QUERY,
);
